import { blue, deepOrange, red } from "@mui/material/colors";

import { getWasteThresholdsForState } from "../WasteShipments/wasteThresholds";

export type DisplayThreshold = {
  name: string;
  /** Threshold value in pounds. */
  thresholdLbs: number;
  color: string;
};

/**
 * Generator-status thresholds for the facility's state, mapped to colored
 * display lines for the main (non-acute) stacked chart.
 *
 * - Values are returned in pounds to match the rest of the Waste app
 *   (shipments tile, generator-status tile, WADM ingest — all lb-denominated).
 *   The federal regulations are written in kg natively (100 kg / 1,000 kg) but
 *   the lb constants (220 / 2,200) are what every other surface in the app
 *   uses, so we stay consistent here.
 * - Federal lines are orange, state lines blue.
 * - Zero-valued thresholds (e.g. "any quantity" VSQG markers) are dropped — a
 *   line at 0 is noise.
 * - The federal LQG-acute threshold is excluded here because it belongs on
 *   the separate acute series, not the main non-acute chart. Use
 *   {@link getAcuteThresholdLbs} for that.
 */
export function getGenerationThresholds(state?: string | null): DisplayThreshold[] {
  if (!state) return [];
  return getWasteThresholdsForState(state)
    .filter((t) => t.threshold > 0 && !t.acute)
    .map((t) => ({
      name: t.name,
      thresholdLbs: t.threshold,
      color: t.type === "federal" ? deepOrange[400] : blue[400],
    }));
}

/** Federal LQG-acute threshold in pounds (~2.2 lb/month). */
export function getAcuteThresholdLbs(state?: string | null): number {
  if (!state) return 0;
  const acute = getWasteThresholdsForState(state).find((t) => t.acute && t.threshold > 0);
  return acute ? acute.threshold : 0;
}

/** Display color for the acute series / annotation. */
export const ACUTE_COLOR = red[600];
